import React, { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Sparkles, Check, X, RefreshCw, CalendarPlus, Pencil } from 'lucide-react';
import * as api from '../../api/client';
import { AppError } from '../../api/request';
import { useConfirm } from '../../context/ConfirmContext';
import { useInstagramAccounts } from '../../context/InstagramAccountContext';
import PageHeader from '../components/PageHeader';
import NoAccountState from '../components/NoAccountState';

const TABS = ['pending_review', 'approved', 'rejected'] as const;
const TAB_KEY: Record<(typeof TABS)[number], string> = {
  pending_review: 'content.tabs.pending',
  approved: 'content.tabs.approved',
  rejected: 'content.tabs.rejected',
};

export default function ContentQueuePage() {
  const { t } = useTranslation();
  const confirm = useConfirm();
  const { activeAccount } = useInstagramAccounts();
  const [tab, setTab] = useState<(typeof TABS)[number]>('pending_review');
  const [drafts, setDrafts] = useState<api.ContentDraft[]>([]);
  const [referenceSources, setReferenceSources] = useState<api.ReferenceSource[]>([]);
  const [selectedRef, setSelectedRef] = useState('');
  const [loading, setLoading] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [busyId, setBusyId] = useState<string | null>(null);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editCaption, setEditCaption] = useState('');
  const [error, setError] = useState('');

  const load = useCallback(async () => {
    if (!activeAccount) {
      setDrafts([]);
      return;
    }
    setLoading(true);
    setDrafts([]);
    try {
      setDrafts(await api.listContentDrafts(activeAccount.id, tab));
    } finally {
      setLoading(false);
    }
  }, [activeAccount, tab]);

  useEffect(() => {
    load();
  }, [load]);

  useEffect(() => {
    setSelectedRef('');
    setEditingId(null);
    setEditCaption('');
    setReferenceSources([]);
    if (!activeAccount) return;
    api.listReferenceSources(activeAccount.id).then(setReferenceSources).catch(() => {});
  }, [activeAccount]);

  async function generate() {
    if (!activeAccount) return;
    setError('');
    setGenerating(true);
    try {
      await api.generateContentDraft(activeAccount.id, selectedRef || undefined);
      setTab('pending_review');
      load();
    } catch (err) {
      setError(err instanceof AppError ? err.message : t('content.errors.generateFailed'));
    } finally {
      setGenerating(false);
    }
  }

  async function act(fn: () => Promise<unknown>, id: string) {
    setError('');
    setBusyId(id);
    try {
      await fn();
      load();
    } catch (err) {
      setError(err instanceof AppError ? err.message : t('content.errors.actionFailed'));
    } finally {
      setBusyId(null);
    }
  }

  async function saveEdit(draft: api.ContentDraft) {
    if (!activeAccount) return;
    await act(() => api.updateContentDraft(activeAccount.id, draft.id, editCaption, draft.hashtags), draft.id);
    setEditingId(null);
  }

  async function schedule(draft: api.ContentDraft) {
    if (!activeAccount) return;
    const ok = await confirm({
      title: t('content.scheduleButton'),
      message: t('content.scheduleConfirm'),
      confirmLabel: t('content.scheduleButton'),
    });
    if (!ok) return;
    await act(() => api.createScheduledPost(activeAccount.id, draft.id), draft.id);
  }

  if (!activeAccount) {
    return <NoAccountState />;
  }

  return (
    <div>
      <PageHeader icon={Sparkles} title={t('content.title')} subtitle={t('content.creditNote')} />

      <div className="card p-4 mb-6 flex flex-wrap items-center gap-3">
        <select className="input max-w-xs" value={selectedRef} onChange={(e) => setSelectedRef(e.target.value)}>
          <option value="">{t('content.noReferenceOption')}</option>
          {referenceSources.filter((r) => r.status === 'analyzed').map((r) => (
            <option key={r.id} value={r.id}>@{r.username}</option>
          ))}
        </select>
        <button onClick={generate} disabled={generating} className="btn-primary">
          <Sparkles className="h-4 w-4" /> {generating ? t('content.generating') : t('content.generateButton')}
        </button>
      </div>

      {error && <p className="mb-4 text-sm text-red-600">{error}</p>}

      <div className="flex gap-2 mb-6">
        {TABS.map((value) => (
          <button
            key={value}
            onClick={() => setTab(value)}
            className={`px-4 py-2 rounded-md text-xs font-semibold transition-colors ${
              tab === value
                ? 'bg-lc-pink text-white dark:text-lc-ink border border-lc-pink'
                : 'bg-white dark:bg-lc-bg border border-[#e8ddd0] dark:border-lc-border text-[#241c14] dark:text-lc-text hover:border-lc-pink/40'
            }`}
          >
            {t(TAB_KEY[value])}
          </button>
        ))}
      </div>

      {loading ? (
        <p className="text-gray-400 dark:text-lc-faint text-sm">{t('common.loading')}</p>
      ) : drafts.length === 0 ? (
        <div className="card border-dashed p-10 text-center">
          <span className="icon-chip mx-auto">
            <Sparkles className="h-5 w-5" />
          </span>
          <p className="mt-4 text-sm text-gray-400 dark:text-lc-faint">{t('content.empty')}</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {drafts.map((d) => (
            <div key={d.id} className="card overflow-hidden flex flex-col hover:shadow-md transition-shadow">
              <img src={d.imageUrl} alt="" className="w-full aspect-square object-cover bg-gray-100 dark:bg-lc-card" />
              <div className="p-4 flex-1 flex flex-col">
                {d.source === 'manual' && (
                  <span className="mb-2 self-start badge bg-[#f0e6f0] text-lc-purple dark:bg-lc-borderStrong dark:text-lc-muted">
                    {t('content.sourceManual')}
                  </span>
                )}
                {editingId === d.id ? (
                  <textarea className="input flex-1 text-sm" rows={4} value={editCaption} onChange={(e) => setEditCaption(e.target.value)} />
                ) : (
                  <p className="text-sm text-gray-700 dark:text-lc-muted flex-1 whitespace-pre-wrap">{d.caption}</p>
                )}
                {d.hashtags?.length > 0 && (
                  <p className="mt-2 flex flex-wrap gap-1">
                    {d.hashtags.map((h) => (
                      <span key={h} className="inline-block text-[10px] font-medium text-lc-purple bg-[#f0e6f0] dark:bg-lc-borderStrong px-2 py-1 rounded">#{h}</span>
                    ))}
                  </p>
                )}
                <div className="mt-3 flex flex-wrap gap-2">
                  {tab === 'pending_review' && editingId !== d.id && (
                    <>
                      <button onClick={() => act(() => api.approveContentDraft(activeAccount.id, d.id), d.id)} disabled={busyId === d.id} className="btn-primary text-xs px-3 py-1.5">
                        <Check className="h-3.5 w-3.5" /> {t('content.approve')}
                      </button>
                      <button onClick={() => act(() => api.rejectContentDraft(activeAccount.id, d.id), d.id)} disabled={busyId === d.id} className="btn-secondary text-xs px-3 py-1.5">
                        <X className="h-3.5 w-3.5" /> {t('content.reject')}
                      </button>
                      <button onClick={() => act(() => api.regenerateContentDraft(activeAccount.id, d.id), d.id)} disabled={busyId === d.id} className="btn-secondary text-xs px-3 py-1.5">
                        <RefreshCw className="h-3.5 w-3.5" /> {t('content.regenerate')}
                      </button>
                      <button onClick={() => { setEditingId(d.id); setEditCaption(d.caption); }} className="btn-secondary text-xs px-3 py-1.5">
                        <Pencil className="h-3.5 w-3.5" /> {t('content.edit')}
                      </button>
                    </>
                  )}
                  {editingId === d.id && (
                    <>
                      <button onClick={() => saveEdit(d)} className="btn-primary text-xs px-3 py-1.5">{t('content.saveEdit')}</button>
                      <button onClick={() => setEditingId(null)} className="btn-secondary text-xs px-3 py-1.5">{t('content.cancelEdit')}</button>
                    </>
                  )}
                  {tab === 'approved' && (
                    <button onClick={() => schedule(d)} disabled={busyId === d.id} className="btn-primary text-xs px-3 py-1.5">
                      <CalendarPlus className="h-3.5 w-3.5" /> {t('content.scheduleButton')}
                    </button>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
